import React, { useState, useRef, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { FaWaze, FaMapMarkerAlt } from 'react-icons/fa';
import {
  MdLocationOn,
  MdSearch,
  MdHome,
  MdLocationCity,
  MdMarkunread,
  MdMap,
  MdEdit,
  MdClose,
  MdCheckCircle,
  MdMyLocation
} from 'react-icons/md';
import translations from '../../translations';

// Custom marker icon for modern look
const customIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-gold.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [30, 46],
  iconAnchor: [15, 46],
  popupAnchor: [1, -40],
  shadowSize: [46, 46]
});

// Component to handle map move events and update location
const MapUpdater = ({ center, onMapMove }) => {
  const map = useMap();
  
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  
  useEffect(() => {
    const handleMoveEnd = () => {
      const mapCenter = map.getCenter();
      onMapMove(mapCenter);
    };
    
    map.on('moveend', handleMoveEnd);
    
    return () => {
      map.off('moveend', handleMoveEnd);
    };
  }, [map, onMapMove]);
  
  return null;
};

function EditLocation({
  locationName,
  setLocationName,
  streetAddress,
  setStreetAddress,
  city,
  setCity,
  state,
  setState,
  postcode,
  setPostcode,
  setOpenModalType
}) {
  const [location, setLocation] = useState({
    lat: 48.8566,
    lng: 2.3522,
    address: ''
  });
  const [addressForm, setAddressForm] = useState({
    venueName: locationName || '',
    streetAddress: streetAddress || '',
    city: city || '',
    state: state || '',
    postcode: postcode || ''
  });
  const [error, setError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(true);
  const autocompleteRef = useRef(null);
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');
  const t = translations[language];


  // Initialize from localStorage
  useEffect(() => {
    const storedDirections = localStorage.getItem('directions');
    if (storedDirections) {
      const [locationData] = JSON.parse(storedDirections);
      if (locationData && locationData.coordinate) {
        const [lat, lng] = locationData.coordinate.split(',').map(Number);

        setLocation({
          lat,
          lng,
          address: locationData.streetAddress,
          venueName: locationData.locationName,
          wazeUrl: locationData.wazeUrl,
          googleMapsUrl: locationData.googleMapsUrl
        });

        setAddressForm({
          venueName: locationData.locationName || '',
          streetAddress: locationData.streetAddress || '',
          city: locationData.city || '',
          state: locationData.state || '',
          postcode: locationData.postcode || ''
        });
      }
    }
  }, []);

  const handleAddressChange = (field, value) => {
    setAddressForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update parent props
    switch (field) {
      case 'venueName':
        setLocationName(value);
        break;
      case 'streetAddress':
        setStreetAddress(value);
        break;
      case 'city':
        setCity(value);
        break;
      case 'state':
        setState(value);
        break;
      case 'postcode':
        setPostcode(value);
        break;
      default:
        break;
    }
    
    if (field === 'venueName') {
      setLocation(prev => ({
        ...prev,
        venueName: value
      }));
    }
  };

  const handleMapMove = (latlng) => {
    const { lat, lng } = latlng;
    console.log('Map moved to:', lat, lng);

    setLocation(prev => ({
      ...prev,
      lat,
      lng,
      address: `${t.selectedLocationPrefix}${lat.toFixed(6)}, ${lng.toFixed(6)}`
    }));

    // Use reverse geocoding to get address details
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      const latlngObj = { lat, lng };

      geocoder.geocode({ location: latlngObj }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const place = results[0];
          console.log('Reverse geocoding result:', place);
          
          setLocation(prev => ({
            ...prev,
            address: place.formatted_address
          }));

          // Update the search input field with the new address
          if (autocompleteRef.current) {
            const input = autocompleteRef.current.querySelector('input');
            if (input) {
              input.value = place.formatted_address || '';
            }
          }

          // Auto-fill address form with reverse geocoded data
          const addressComponents = parseAddressComponents(place);
          const streetAddress = `${addressComponents.streetNumber} ${addressComponents.route}`.trim();
          
          const newAddressForm = {
            venueName: addressForm.venueName,
            streetAddress: streetAddress || '',
            city: addressComponents.city || '',
            state: addressComponents.state || '',
            postcode: addressComponents.postcode || ''
          };
          
          setAddressForm(newAddressForm);
          
          // Update parent props
          setStreetAddress(newAddressForm.streetAddress);
          setCity(newAddressForm.city);
          setState(newAddressForm.state);
          setPostcode(newAddressForm.postcode);
        }
      });
    }
  };

  const parseAddressComponents = (place) => {
    const components = place.address_components || [];
    const parsed = {
      streetNumber: '',
      route: '',
      city: '',
      state: '',
      postcode: '',
      country: ''
    };

    components.forEach(component => {
      const types = component.types;
      
      if (types.includes('street_number')) {
        parsed.streetNumber = component.long_name;
      } else if (types.includes('route')) {
        parsed.route = component.long_name;
      } else if (types.includes('locality') || types.includes('administrative_area_level_2')) {
        parsed.city = component.long_name;
      } else if (types.includes('administrative_area_level_1')) {
        parsed.state = component.long_name;
      } else if (types.includes('postal_code')) {
        parsed.postcode = component.long_name;
      } else if (types.includes('country')) {
        parsed.country = component.long_name;
      }
    });

    return parsed;
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError(t.geolocationNotSupported);
      return;
    }

    setIsGettingLocation(true);
    setError('');

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        console.log('Current location:', latitude, longitude);
        
        // Update location state
        setLocation({
          lat: latitude,
          lng: longitude,
          address: `${t.currentLocationPrefix}${latitude.toFixed(6)}, ${longitude.toFixed(6)}`
        });

        // Use reverse geocoding to get address details
        if (window.google && window.google.maps) {
          const geocoder = new window.google.maps.Geocoder();
          const latlng = { lat: latitude, lng: longitude };

          geocoder.geocode({ location: latlng }, (results, status) => {
            if (status === 'OK' && results[0]) {
              const place = results[0];
              console.log('Reverse geocoding result:', place);
              
              setLocation(prev => ({
                ...prev,
                address: place.formatted_address
              }));

              // Update the search input field with the current location address
              if (autocompleteRef.current) {
                const input = autocompleteRef.current.querySelector('input');
                if (input) {
                  input.value = place.formatted_address || '';
                }
              }

              // Auto-fill address form with reverse geocoded data
              const addressComponents = parseAddressComponents(place);
              const streetAddress = `${addressComponents.streetNumber} ${addressComponents.route}`.trim();
              
              const newAddressForm = {
                venueName: addressForm.venueName,
                streetAddress: streetAddress || '',
                city: addressComponents.city || '',
                state: addressComponents.state || '',
                postcode: addressComponents.postcode || ''
              };
              
              setAddressForm(newAddressForm);
              
              // Update parent props
              setStreetAddress(newAddressForm.streetAddress);
              setCity(newAddressForm.city);
              setState(newAddressForm.state);
              setPostcode(newAddressForm.postcode);
            }
          });
        }

        setIsGettingLocation(false);
        setShowLocationPrompt(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        let errorMessage = t.unableToGetCurrentLocation;

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += t.allowLocationAccess;
            setShowLocationPrompt(true);
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += t.locationUnavailable;
            break;
          case error.TIMEOUT:
            errorMessage += t.locationTimeout;
            break;
          default:
            errorMessage += t.unknownError;
            break;
        }

        setError(errorMessage);
        setIsGettingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000
      }
    );
  };

  const handleConfirmLocation = () => {
    const fullAddress = `${addressForm.streetAddress}, ${addressForm.city}, ${addressForm.postcode} ${addressForm.state}, Malaysia`;
    const wazeUrl = `https://waze.com/ul?ll=${location.lat},${location.lng}`;
    const googleMapsUrl = `https://www.google.com/maps?q=${location.lat},${location.lng}`;
    
    const locationData = {
      locationName: addressForm.venueName,
      streetAddress: addressForm.streetAddress,
      city: addressForm.city,
      state: addressForm.state,
      postcode: addressForm.postcode,
      coordinate: `${location.lat},${location.lng}`,
      wazeUrl,
      googleMapsUrl
    };

    // Save to localStorage
    localStorage.setItem('directions', JSON.stringify([locationData]));
    localStorage.setItem('location', addressForm.venueName);
    localStorage.setItem('streetAddress', addressForm.streetAddress);
    localStorage.setItem('city', addressForm.city);
    localStorage.setItem('state', addressForm.state);
    localStorage.setItem('postcode', addressForm.postcode);

    setLocation(prev => ({
      ...prev,
      address: fullAddress,
      wazeUrl,
      googleMapsUrl
    }));
    
    setOpenModalType(null);
  };

  useEffect(() => {
    const initializeAutocomplete = () => {
      try {
        if (!window.google || !window.google.maps || !window.google.maps.places) {
          console.error('Google Maps not loaded');
          setError(t.googleMapsFailed);
          return;
        }

        console.log('Using traditional Autocomplete API (reliable implementation)');

        const input = document.createElement('input');
        input.type = 'text';
        input.placeholder = t.searchLocation;
        input.className = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent';
        
        if (autocompleteRef.current) {
          autocompleteRef.current.innerHTML = '';
          autocompleteRef.current.appendChild(input);
          
          const autocomplete = new window.google.maps.places.Autocomplete(input, {
            region: 'eu',
            fields: ['geometry', 'formatted_address', 'name', 'address_components']
          });
          
          autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            console.log('Place changed:', place);
            
            if (!place.geometry) {
              console.warn('No geometry found for place:', place);
              setError(t.unableToFindCoordinates);
              return;
            }
            
            if (place.geometry && place.geometry.location) {
              const newLocation = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng(),
                address: place.formatted_address || place.name || ''
              };
              console.log('Setting new location:', newLocation);
              setLocation(newLocation);
              
              // Auto-fill address form
              const addressComponents = parseAddressComponents(place);
              console.log('Parsed address components:', addressComponents);
              
              const streetAddress = `${addressComponents.streetNumber} ${addressComponents.route}`.trim();
              
              const newAddressForm = {
                venueName: addressForm.venueName,
                streetAddress: streetAddress || '',
                city: addressComponents.city || '',
                state: addressComponents.state || '',
                postcode: addressComponents.postcode || ''
              };
              
              setAddressForm(newAddressForm);
              
              // Update parent props
              setStreetAddress(newAddressForm.streetAddress);
              setCity(newAddressForm.city);
              setState(newAddressForm.state);
              setPostcode(newAddressForm.postcode);
              
              setError('');
            }
          });
        }
        setError('');
      } catch (err) {
        console.error('Error initializing Google Maps:', err);
        setError(t.failedToInitializeSearch);
      }
    };
    
    window.initMap = initializeAutocomplete;

    if (!window.google) {
        setError(t.loadingGoogleMaps);
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async&callback=initMap`;
        script.async = true;
        script.defer = true;
        document.body.appendChild(script);
    } else if (window.google && window.google.maps) {
        initializeAutocomplete();
    }
    
    return () => {
        const scripts = document.querySelectorAll(`script[src*="maps.googleapis.com"]`);
        scripts.forEach(s => s.remove());
        if (window.initMap === initializeAutocomplete) {
             delete window.initMap;
        }
    };
  }, []);

  return (
    <div className="max-w-md mx-auto bg-white min-h-screen">
      {/* Map Section */}
      <div className="h-[300px] border-b relative rounded-b-xl overflow-hidden shadow-lg" style={{ borderColor: '#f3e8c9' }}>
        <MapContainer
          center={[location.lat, location.lng]}
          zoom={16}
          style={{ height: '100%', width: '100%' }}
          key={`${location.lat}-${location.lng}`}
          className="rounded-b-xl"
        >
          <MapUpdater center={[location.lat, location.lng]} onMapMove={handleMapMove} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
          />
        </MapContainer>
        
        {/* Current Location Button */}
        <button
          onClick={getCurrentLocation}
          disabled={isGettingLocation}
          className="absolute top-4 right-4 bg-white hover:bg-gray-50 disabled:bg-gray-100 text-gray-700 p-3 rounded-full shadow-lg border border-gray-200 transition-all duration-200 z-[1000] hover:shadow-xl"
          title={t.useCurrentLocation}
        >
          {isGettingLocation ? (
            <div className="animate-spin w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full"></div>
          ) : (
            <MdMyLocation className="text-lg" />
          )}
        </button>
        
        {/* Center Pin - Fixed position */}
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-[1000] pointer-events-none">
          <div className="relative">
            <MdLocationOn className="text-4xl text-red-500 drop-shadow-lg" />
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1 w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
          </div>
        </div>
        
        {/* Instruction text */}
        <div className="absolute bottom-4 left-4 right-4 bg-white bg-opacity-90 rounded-lg p-2 z-[1000] pointer-events-none">
          <p className="text-xs text-gray-600 text-center">
            {t.dragMapInstruction}
          </p>
        </div>
      </div>

      {/* Address Form */}
      <div className="p-4 space-y-4">
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
            <MdSearch />
            <span>{t.searchLocation}</span>
          </label>
          <div className="flex space-x-2 items-stretch">
            <div
              ref={autocompleteRef}
              className="flex-1 min-w-0"
              style={{
                '--gmp-primary-color': '#ef4444',
                '--gmp-outline-color': '#d1d5db'
              }}
            />
            <button
              onClick={getCurrentLocation}
              disabled={isGettingLocation}
              className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-4 rounded-lg transition-colors flex items-center justify-center flex-shrink-0"
              title={t.useCurrentLocation}
            >
              {isGettingLocation ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
              ) : (
                <MdMyLocation className="text-lg" />
              )}
            </button>
          </div>
        </div>
        
        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
            <MdLocationCity />
            <span>{t.venueName}</span>
          </label>
          <input
            type="text"
            value={addressForm.venueName || ''}
            onChange={(e) => handleAddressChange('venueName', e.target.value)}
            placeholder={t.enterVenueName}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
            <MdHome />
            <span>{t.streetAddress}</span>
          </label>
          <input
            type="text"
            value={addressForm.streetAddress}
            onChange={(e) => handleAddressChange('streetAddress', e.target.value)}
            placeholder={t.enterStreetAddress}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
              <MdLocationCity />
              <span>{t.city}</span>
            </label>
            <input
              type="text"
              value={addressForm.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              placeholder={t.cityPlaceholder}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
              <MdMarkunread />
              <span>{t.postcode}</span>
            </label>
            <input
              type="text"
              value={addressForm.postcode}
              onChange={(e) => handleAddressChange('postcode', e.target.value)}
              placeholder={t.postcodePlaceholder}
              className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700 mb-2 flex items-center space-x-1">
            <MdMap />
            <span>{t.state}</span>
          </label>
          <input
            type="text"
            value={addressForm.state}
            onChange={(e) => handleAddressChange('state', e.target.value)}
            placeholder={t.state}
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
          />
        </div>

        {/* Confirm Button */}
        <button
          onClick={handleConfirmLocation}
          disabled={!addressForm.venueName || !addressForm.streetAddress || !addressForm.city || !addressForm.state || !addressForm.postcode}
          className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-4 px-4 rounded-lg transition-colors mt-6 flex items-center justify-center space-x-2"
        >
          <MdCheckCircle />
          <span>{t.updateLocation}</span>
        </button>
      </div>
    </div>
  );
}

export default EditLocation;
